import { Control, Icon, Image, Tooltip, Typography } from '@modulor/react';
import clx from 'classnames';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useOnClickOutside } from 'Modulor/hooks';
import type { FC } from 'react';
import { useRef, useState } from 'react';

import { PendingWithdrawalTooltip } from './types';

interface PendingWithdrawalCardProps {
  orderId: number;
  logo?: string;
  name: string;
  amount: string;
  returnAmount?: string;
  remainAmount?: string;
  checked: boolean;
  disabled?: boolean;
  errorText?: string;
  onToggle: (orderId: number) => void;
}

const EMPTY_VALUE = '—';

export const PendingWithdrawalCard: FC<PendingWithdrawalCardProps> = ({
  orderId,
  logo,
  name,
  amount,
  returnAmount,
  remainAmount,
  checked,
  disabled,
  errorText,
  onToggle,
}) => {
  const { translate } = useSkeletonTranslation();
  const [openedTooltip, setOpenedTooltip] = useState<PendingWithdrawalTooltip | null>(null);
  const openedTriggerRef = useRef<HTMLSpanElement>(null);
  useOnClickOutside(openedTriggerRef, () => setOpenedTooltip(null));

  const toggleTooltip = (id: PendingWithdrawalTooltip) => setOpenedTooltip((current) => (current === id ? null : id));

  const rows: { label: string; value: string; tooltipId?: PendingWithdrawalTooltip; tooltip?: string }[] = [
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.WITHDRAWAL_AMOUNT'),
      value: amount,
    },
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.RETURN'),
      value: returnAmount ?? EMPTY_VALUE,
      tooltipId: PendingWithdrawalTooltip.returnAmount,
      tooltip: translate('PH.DEPOSIT.PARTIAL_REVERSE.RETURN_TOOLTIP'),
    },
    {
      label: translate('PH.DEPOSIT.PARTIAL_REVERSE.REMAIN'),
      value: remainAmount ?? EMPTY_VALUE,
      tooltipId: PendingWithdrawalTooltip.remainAmount,
      tooltip: translate('PH.DEPOSIT.PARTIAL_REVERSE.REMAIN_TOOLTIP'),
    },
  ];

  return (
    <div
      className={clx('pending-withdrawal-card', { 'pending-withdrawal-card--disabled': disabled })}
      data-testid="pending-withdrawal-card"
    >
      <div className="pending-withdrawal-card__main">
        <div className="pending-withdrawal-card__logo">
          <Image src={logo} alt={name} width="24" height="24" />
        </div>
        <div className="pending-withdrawal-card__rows">
          {rows.map(({ label, value, tooltipId, tooltip }) => (
            <div key={label} className="pending-withdrawal-card__row">
              <span className="pending-withdrawal-card__label">
                {tooltipId && tooltip ? (
                  <Tooltip
                    ref={openedTooltip === tooltipId ? openedTriggerRef : undefined}
                    event="click"
                    show={openedTooltip === tooltipId}
                    onClick={() => toggleTooltip(tooltipId)}
                    placement="top"
                    heading=""
                    text={tooltip}
                    maxTextLength={300}
                    className={clx('pending-withdrawal-card__tooltip', {
                      'pending-withdrawal-card__tooltip--opened': openedTooltip === tooltipId,
                    })}
                  >
                    {[
                      <Typography
                        key="label"
                        variant="subhead-regular"
                        tag="span"
                        className="pending-withdrawal-card__label-text"
                      >
                        {label}
                      </Typography>,
                      <Icon key="icon" name="info_circle_outlined" size={12} />,
                    ]}
                  </Tooltip>
                ) : (
                  <Typography variant="subhead-regular" tag="span" className="pending-withdrawal-card__label-text">
                    {label}
                  </Typography>
                )}
              </span>
              <Typography variant="subhead-regular" tag="span" className="pending-withdrawal-card__value">
                {value}
              </Typography>
            </div>
          ))}
        </div>
        <div className="pending-withdrawal-card__checkbox">
          <Control
            type="checkbox"
            name={`pending-withdrawal-${orderId}`}
            checked={checked}
            disabled={disabled}
            onChange={() => onToggle(orderId)}
          />
        </div>
      </div>
      {errorText && (
        <Typography variant="subhead-regular" tag="p" className="pending-withdrawal-card__error">
          {errorText}
        </Typography>
      )}
    </div>
  );
};
